import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import QuestionItem from '@/components/QuestionItem';
import ProgressBar from '@/components/ProgressBar';
import Logo from '@/components/Logo';
import { getQuestions } from '@/data/questions';
import { Answer, SurveyResponse } from '@/types/survey';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, ArrowRight, Bot, Lightbulb, BarChart2, MessageCircle, Info, AlertCircle } from 'lucide-react';
import AIDataGathering from '@/components/AIDataGathering';
import AIChatSurvey from '@/components/AIChatSurvey';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useLanguage } from '@/contexts/LanguageContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { motion, AnimatePresence } from 'framer-motion';

const Survey = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t, language } = useLanguage();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState<SurveyResponse>({});
  const [industry, setIndustry] = useState<string>('');
  const [projectName, setProjectName] = useState<string>('');
  const [activeTab, setActiveTab] = useState<string>('manual');
  const [validationError, setValidationError] = useState<string | null>(null);
  
  const questions = getQuestions(language);

  useEffect(() => {
    const selectedIndustry = sessionStorage.getItem('nev-industry');
    const savedProjectName = sessionStorage.getItem('nev-project-name');
    
    if (!selectedIndustry) {
      toast({
        title: t('industry.title'),
        description: t('industry.subtitle'),
        variant: "destructive"
      });
      navigate('/industry');
      return;
    }
    
    setIndustry(selectedIndustry);
    setProjectName(savedProjectName || 'Unnamed Project');

    const savedResponses = sessionStorage.getItem('nev-responses');
    if (savedResponses) {
      try {
        const parsed = JSON.parse(savedResponses);
        setResponses(parsed);
      } catch (error) {
        console.error('Error parsing saved responses:', error);
      }
    }
  }, [navigate, toast, t]);

  useEffect(() => {
    // Clear validation error when answer changes
    setValidationError(null);
  }, [responses]);

  const currentQuestion = questions[currentQuestionIndex];

  const handleAnswer = (id: number, value: Answer) => {
    console.log("Answer selected:", id, value);
    const updatedResponses = {
      ...responses,
      [id]: value
    };
    setResponses(updatedResponses);
    setValidationError(null);
    
    sessionStorage.setItem('nev-responses', JSON.stringify(updatedResponses));
  };
  
  const handleChatAnswer = (questionId: number, answer: Answer) => {
    const updatedResponses = {
      ...responses,
      [questionId]: answer
    };
    setResponses(updatedResponses);
    
    sessionStorage.setItem('nev-responses', JSON.stringify(updatedResponses));
  };

  const meetsMinimumWordCount = (questionId: number, answer?: Answer): boolean => {
    const question = questions.find(q => q.id === questionId);
    if (!question || question.type !== 'text' || !question.minWords || !answer) {
      return true;
    }
    
    const text = answer as string;
    const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;
    return wordCount >= question.minWords;
  };

  const goToNextQuestion = () => {
    if (!responses[currentQuestion.id] && currentQuestion.required) {
      setValidationError("Please answer this question before continuing");
      toast({
        title: "Answer required",
        description: "Please answer the current question before proceeding.",
        variant: "destructive"
      });
      return;
    }
    
    if (
      currentQuestion.type === 'text' && 
      currentQuestion.minWords && 
      !meetsMinimumWordCount(currentQuestion.id, responses[currentQuestion.id])
    ) {
      setValidationError(`Please provide at least ${currentQuestion.minWords} words for a meaningful response.`);
      toast({
        title: "More detail needed",
        description: `Please provide at least ${currentQuestion.minWords} words for a meaningful response.`,
        variant: "destructive"
      });
      return;
    }

    let nextIndex = currentQuestionIndex + 1;
    while (
      nextIndex < questions.length && 
      !shouldShowQuestion(nextIndex)
    ) {
      nextIndex++;
    }

    if (nextIndex < questions.length) {
      setCurrentQuestionIndex(nextIndex);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      sessionStorage.setItem('nev-responses', JSON.stringify(responses));
      console.log('Survey completed, navigating to results page');
      navigate('/results');
    }
  };

  const goToPreviousQuestion = () => {
    let prevIndex = currentQuestionIndex - 1;
    while (
      prevIndex >= 0 && 
      !shouldShowQuestion(prevIndex)
    ) {
      prevIndex--;
    }

    if (prevIndex >= 0) {
      setCurrentQuestionIndex(prevIndex);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleAIGatheredData = (aiResponses: SurveyResponse) => {
    setResponses(aiResponses);
    sessionStorage.setItem('nev-responses', JSON.stringify(aiResponses));
    
    setActiveTab('manual');
    setCurrentQuestionIndex(0);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    toast({
      title: t('survey.ai.applied'),
      description: t('survey.ai.description'),
    });
  };
  
  const handleChatComplete = () => {
    navigate('/results');
  };

  const isLastQuestion = currentQuestionIndex === questions.length - 1;
  const allQuestionsAnswered = questions.every(q => !q.required || responses[q.id] !== undefined);
  
  const shouldShowQuestion = (questionIndex: number) => {
    const question = questions[questionIndex];
    if (!question.conditional) return true;
    
    const dependentAnswer = responses[question.conditional.dependsOn];
    return dependentAnswer === question.conditional.showIfValue;
  };

  const getCategoryDescription = (category: string) => {
    switch(category) {
      case 'ee': 
        return "Measures the strength of user emotional connection, brand loyalty, and community engagement";
      case 'lq': 
        return "Evaluates transformative impact, long-term industry relevance, and legacy value";
      case 'fc': 
        return "Analyzes friction points and barriers to engagement (lower is better)";
      case 'tb': 
        return "Measures trustworthiness, transparency, and consistency in delivering promises";
      case 'bc': 
        return "Evaluates conversion to tangible actions like investment or advocacy";
      case 'ea': 
        return "Measures the strength of user emotional connection, brand loyalty, and community engagement";
      case 'elv': 
        return "Evaluates transformative impact, long-term industry relevance, and legacy value";
      case 'l': 
        return "Analyzes friction points and barriers to engagement (lower is better)";
      default: 
        return "";
    }
  };

  const getCategoryColor = (category: string) => {
    switch(category) {
      case 'ea': return { bg: 'bg-purple-100', text: 'text-purple-600', emoji: '💜' };
      case 'elv': return { bg: 'bg-blue-100', text: 'text-blue-600', emoji: '🔷' };
      case 'l': return { bg: 'bg-red-100', text: 'text-red-600', emoji: '🔴' };
      case 'tb': return { bg: 'bg-green-100', text: 'text-green-600', emoji: '🟢' };
      case 'bc': return { bg: 'bg-amber-100', text: 'text-amber-600', emoji: '🟠' };
      case 'strategy': return { bg: 'bg-indigo-100', text: 'text-indigo-600', emoji: '🔮' };
      default: return { bg: 'bg-gray-100', text: 'text-gray-600', emoji: '⚪' };
    }
  };

  const aiMethodologyInstructions = {
    purpose: "The AI Methodology Guide helps you understand the survey's structure and how to provide meaningful answers.",
    tips: [
      "Take time to read each question carefully.",
      "Provide detailed and honest responses.",
      "Context is key - explain your reasoning.",
      "If unsure, provide your best understanding."
    ],
    example: {
      question: "How does your project create emotional value?",
      goodAnswer: "Our project creates emotional value by building a community-driven platform that empowers users to share their stories and support each other. We prioritize transparency, user experience, and creating meaningful connections.",
      poorAnswer: "Our project is good."
    }
  };

  if (!currentQuestion) return null;
  
  return (
    <div 
      className="min-h-screen py-8 px-4"
      style={{
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e9f2 100%)',
        backgroundAttachment: 'fixed'
      }}
    >
      <div className="max-w-2xl mx-auto">
        {/* Background element with glassmorphism */}
        <div 
          className="absolute top-16 left-1/2 transform -translate-x-1/2 w-full max-w-2xl backdrop-blur-md bg-white/50 rounded-xl border border-white/20 shadow-xl" 
          style={{ height: '220px', zIndex: 0 }}
        />
        
        <div className="w-full flex justify-end mb-2 relative z-10">
          <LanguageSwitcher />
        </div>
        
        <motion.div 
          className="mb-8 flex flex-col items-center relative z-10"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Logo className="mb-4" />
          
          <div className="mb-2 flex items-center justify-center">
            <div className="h-10 w-10 flex items-center justify-center rounded-full mr-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md">
              2
            </div>
            <div className="text-sm font-medium text-gray-600">{t('step.2')}</div>
          </div>
          <h1 className="text-3xl font-bold mb-2 bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
            {t('survey.title')}
          </h1>
          <p className="text-gray-500 backdrop-blur-sm bg-white/30 px-4 py-2 rounded-full shadow-sm border border-white/50">
            {t('industry.projectName')} <span className="font-medium">{projectName}</span> | 
            {t('industry.title')} <span className="font-medium">{industry}</span>
          </p>
        </motion.div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full mb-6">
          <TabsList className="grid w-full grid-cols-3 backdrop-blur-sm bg-white/50 p-1 rounded-lg border border-white/20">
            <TabsTrigger value="manual" className="data-[state=active]:bg-white data-[state=active]:shadow-md transition-all rounded-md">
              {t('survey.manual')}
            </TabsTrigger>
            <TabsTrigger value="chat" className="flex items-center gap-1 data-[state=active]:bg-white data-[state=active]:shadow-md transition-all rounded-md">
              <MessageCircle className="h-4 w-4" /> {t('survey.chat.title')}
            </TabsTrigger>
            <TabsTrigger value="ai" className="flex items-center gap-1 data-[state=active]:bg-white data-[state=active]:shadow-md transition-all rounded-md">
              <Bot className="h-4 w-4" /> {t('survey.ai.guide')}
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="manual" className="mt-4">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentQuestionIndex}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {/* Display the section header if present */}
                {currentQuestion?.sectionHeader && (
                  <motion.div 
                    className="mb-5 bg-gradient-to-r from-blue-50 to-indigo-50 p-5 rounded-xl border border-blue-100 shadow-md"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1, duration: 0.4 }}
                  >
                    <h3 className="text-lg font-semibold text-blue-700">{currentQuestion.sectionHeader}</h3>
                  </motion.div>
                )}
                
                <Card className="mb-6 backdrop-blur-xl bg-white/80 border border-white/50 shadow-xl rounded-xl overflow-hidden">
                  <CardHeader className="bg-gradient-to-r from-slate-50 to-blue-50 border-b">
                    <CardTitle className="text-lg">
                      <div className="flex justify-between items-center">
                        <span className="flex items-center">
                          {currentQuestion?.category && (
                            <span className={`mr-3 p-2 rounded-lg ${getCategoryColor(currentQuestion?.category).bg}`}>
                              {getCategoryColor(currentQuestion?.category).emoji}
                            </span>
                          )}
                          <span className="text-gray-800">
                            {currentQuestion.id}. {currentQuestion.text}
                          </span>
                        </span>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-blue-100/50">
                                <Lightbulb className="h-4 w-4 text-amber-500" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs bg-white/95 backdrop-blur-md border border-slate-100 shadow-lg p-4">
                              <p className="text-sm font-medium mb-1 text-blue-700">
                                {getCategoryDescription(currentQuestion?.category)}
                              </p>
                              <p className="text-xs text-gray-500">
                                Try the AI Methodology Guide tab for detailed explanations and guidance for all questions
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="py-6">
                    <QuestionItem
                      question={currentQuestion}
                      answer={responses[currentQuestion.id] || ''}
                      onChange={handleAnswer}
                    />

                    {/* Real-time validation error */}
                    <AnimatePresence>
                      {validationError && (
                        <motion.div 
                          className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                        >
                          <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                          <p>{validationError}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </CardContent>
                  <CardFooter className="flex justify-between pt-4 border-t bg-gradient-to-r from-slate-50 to-blue-50">
                    <Button
                      variant="outline"
                      onClick={goToPreviousQuestion}
                      disabled={currentQuestionIndex === 0}
                      className="bg-white hover:bg-slate-100"
                    >
                      <ArrowLeft className="mr-2 h-4 w-4" /> {t('nav.previous')}
                    </Button>
                    <div className="flex-1 mx-4">
                      <ProgressBar current={currentQuestionIndex + 1} total={questions.length} />
                    </div>
                    <Button 
                      onClick={goToNextQuestion}
                      className={isLastQuestion ? 
                        "bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-md" : 
                        "bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-md"
                      }
                    >
                      {isLastQuestion ? t('nav.seeResults') : t('nav.next')} {isLastQuestion ? <BarChart2 className="ml-2 h-4 w-4" /> : <ArrowRight className="ml-2 h-4 w-4" />}
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>
            </AnimatePresence>
          </TabsContent>
          
          <TabsContent value="chat" className="mt-4">
            <Card className="mb-6 h-[600px] flex flex-col backdrop-blur-xl bg-white/80 border border-white/50 shadow-lg rounded-xl overflow-hidden">
              <CardHeader className="border-b bg-gradient-to-r from-slate-50 to-blue-50">
                <CardTitle className="text-lg flex items-center">
                  <MessageCircle className="h-5 w-5 mr-2 text-blue-500" /> 
                  BEAM AI Survey
                </CardTitle>
              </CardHeader>
              <CardContent className="flex-1 p-0 overflow-hidden">
                <AIChatSurvey 
                  questions={questions}
                  responses={responses}
                  onAnswer={handleChatAnswer}
                  onComplete={handleChatComplete}
                />
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="ai" className="mt-4">
            {/* AI Methodology Guide */}
            <div className="p-6 backdrop-blur-lg bg-blue-50/80 rounded-xl border border-blue-200 shadow-md mb-6">
              <h3 className="text-lg font-bold mb-3 flex items-center text-blue-700">
                <Info className="mr-2 text-blue-600" /> AI Methodology Guide
              </h3>
              <p className="mb-4 text-blue-800">{t('survey.ai.methodology.description')}</p>
              
              <div className="mb-4">
                <h4 className="font-semibold mb-2 text-blue-700">Survey Response Tips:</h4>
                <ul className="list-disc pl-5 space-y-2 text-blue-700">
                  {aiMethodologyInstructions.tips.map((tip, index) => (
                    <li key={index}>{tip}</li>
                  ))}
                </ul>
              </div>

              <div className="bg-white/80 backdrop-blur-sm p-5 rounded-xl border border-blue-100 shadow-sm">
                <h4 className="font-semibold mb-2 text-gray-700">Example Response:</h4>
                <p className="mb-2"><strong>Question:</strong> {aiMethodologyInstructions.example.question}</p>
                
                <div className="mb-3 bg-green-50/70 p-3 rounded-lg border border-green-100">
                  <strong className="text-green-600">Good Answer:</strong>
                  <p className="italic text-green-800 pl-2 mt-1">
                    "{aiMethodologyInstructions.example.goodAnswer}"
                  </p>
                </div>
                
                <div className="bg-red-50/70 p-3 rounded-lg border border-red-100">
                  <strong className="text-red-600">Poor Answer:</strong>
                  <p className="italic text-red-800 pl-2 mt-1">
                    "{aiMethodologyInstructions.example.poorAnswer}"
                  </p>
                </div>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <AIDataGathering 
                projectName={projectName}
                industry={industry}
                questions={questions}
                onDataGathered={handleAIGatheredData}
              />
            </motion.div>
            
            {allQuestionsAnswered && (
              <div className="mt-6 text-center">
                <Button 
                  onClick={() => navigate('/results')} 
                  className="flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-md px-6 py-6 text-lg"
                >
                  <BarChart2 className="h-5 w-5" /> {t('nav.seeResults')}
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>

        <motion.div 
          className="text-center text-sm text-gray-500 backdrop-blur-sm bg-white/40 p-4 rounded-xl border border-white/30 shadow-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.5 }}
        >
          <p>{t('survey.data.local')}</p>
          <p className="mt-2 text-xs">This platform is powered by BaaBaa Emotive Asset Management Group</p>
          <p className="text-xs mt-1">Assessment consists of 19 questions across multiple dimensions</p>
        </motion.div>
      </div>
    </div>
  );
};

export default Survey;
